import { useMemo } from "react";
import { Task } from "@common/types/task";
import { useIntegrationConnections } from "@api/integrationConnection.api";
import { BrokenConnectionLink } from "@components/financialConnection/BrokenConnectionLink";
import { Button } from "@design-system/Button/Button";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import { useViewOnlyTooltipReason } from "@hooks/permissions/useViewOnlyTooltipReason";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { useTaskPlaidConnection } from "../shared/useTaskPlaidConnection";
import { getConnectionMetadata } from "../TaskBody.lib";

interface Props {
    selectedTask: Task;
}

const TaskBrokenConnectionAction = ({ selectedTask }: Props) => {
    const { connectionId, isIntegrationConnectionId } =
        getConnectionMetadata(selectedTask);

    const { data: integrationConnections } = useIntegrationConnections();
    const { plaidConnection, isLoading } = useTaskPlaidConnection(
        isIntegrationConnectionId ? null : connectionId,
    );
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const reconnectTooltip = useViewOnlyTooltipReason(
        "Only connection owner can reconnect",
    );

    const integrationConnection = useMemo(() => {
        if (!connectionId || !isIntegrationConnectionId) {
            return null;
        }
        return (
            integrationConnections?.find(
                (connection) => connection.id === connectionId,
            ) ?? null
        );
    }, [connectionId, integrationConnections, isIntegrationConnectionId]);

    if (isLoading) {
        return null;
    }

    if (isIntegrationConnectionId && integrationConnection) {
        return (
            <div
                onClickCapture={(e) => {
                    if (refuseIfViewOnly()) {
                        e.preventDefault();
                        e.stopPropagation();
                    }
                }}
            >
                <BrokenConnectionLink
                    connection={integrationConnection}
                    integrationTriggerContent="Reconnect"
                    integrationTriggerVariant="primary"
                    integrationTriggerSize="md"
                    integrationTriggerLeftIcon={null}
                />
            </div>
        );
    }

    if (!connectionId || isIntegrationConnectionId) {
        return null;
    }

    if (!plaidConnection) {
        return (
            <Tooltip content={reconnectTooltip}>
                <Button disabled variant="primary" size="md">
                    Reconnect
                </Button>
            </Tooltip>
        );
    }

    const integrationAccount = plaidConnection.accounts?.[0];

    return (
        <div
            onClickCapture={(e) => {
                if (refuseIfViewOnly()) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }}
        >
            <BrokenConnectionLink
                connection={plaidConnection}
                integrationAccount={integrationAccount}
                integrationTriggerContent="Reconnect"
                integrationTriggerVariant="primary"
                integrationTriggerSize="md"
                integrationTriggerLeftIcon={null}
            />
        </div>
    );
};

export const BrokenConnectionCommandIsland = ({ selectedTask }: Props) => (
    <CommandIsland alignEnd className="mx-3" sticky>
        <TaskBrokenConnectionAction selectedTask={selectedTask} />
    </CommandIsland>
);
